import React from 'react';
import type { ActivityLogEntry } from '../../types/ActivityLog';
import { formatActivityAction } from '../../types/ActivityLog';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useChildrenNames } from '../../hooks/useChildrenNames';
import { useFamilyStore } from '../../stores/familyStore';

interface ActivityLogListProps {
  activities: ActivityLogEntry[];
  isLoading: boolean;
  error: string | null;
}

export const ActivityLogList: React.FC<ActivityLogListProps> = ({ activities, isLoading, error }) => {
  const { getChildName } = useChildrenNames();
  const { family } = useFamilyStore();
  
  // Funkcja formatująca szczegóły aktywności
  const formatActivityDetails = (entry: ActivityLogEntry): string => {
    // Nie powielamy e‑maila – jest pokazywany w stopce; tutaj bez aktora
    if (!entry.metadata) {
      return entry.details || '';
    }
    
    const childId = entry.metadata.child_id;
    const childName = getChildName(childId);
    
    // Wyciągnij datę z entity_name
    const extractDate = (entityName?: string): string => {
      if (!entityName) return '';
      const match = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : '';
    };
    
    const date = extractDate(entry.entity_name);

    // Spróbuj zbudować opis zmiany from→to na podstawie znanych kluczy metadanych
    const getChangeFromTo = (m: Record<string, any> | undefined): string => {
      if (!m) return '';
      const pairs: Array<[string, string]> = [
        ['old_location', 'new_location'],
        ['old_assignment', 'new_assignment'],
        ['previous_assignment', 'current_assignment'],
        ['from_state', 'to_state'],
        ['from', 'to'],
        ['old_value', 'new_value']
      ];
      const formatValue = (v: any): string => {
        if (v === 'home_a') return family?.home_a_name || 'Dom A';
        if (v === 'home_b') return family?.home_b_name || 'Dom B';
        if (v === null || v === undefined || v === '') return '—';
        if (typeof v === 'string') return v;
        try { return JSON.stringify(v); } catch { return String(v); }
      };
      for (const [fromKey, toKey] of pairs) {
        if (m[fromKey] !== undefined || m[toKey] !== undefined) {
          const fromVal = formatValue(m[fromKey]);
          const toVal = formatValue(m[toKey]);
          if (fromVal || toVal) return `${fromVal || '—'} → ${toVal || '—'}`;
        }
      }
      return '';
    };

    const change = getChangeFromTo(entry.metadata);

    // Parsowanie tytułu i daty z entity_name lub metadata.start_at (dla events/notes/expenses)
    const parseTitleAndDate = (entityName?: string, meta?: Record<string, any>): { title?: string; date?: string } => {
      let parsedTitle: string | undefined;
      let parsedDate: string | undefined;
      if (entityName && entityName.includes(' @ ')) {
        const lastAt = entityName.lastIndexOf(' @ ');
        parsedTitle = entityName.slice(0, lastAt).trim() || undefined;
        parsedDate = entityName.slice(lastAt + 3).trim() || undefined;
      } else if (entityName) {
        const m = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) parsedDate = m[1];
      }
      if (!parsedDate && meta?.start_at) {
        try { parsedDate = new Date(meta.start_at).toISOString().slice(0,10); } catch {}
      }
      return { title: parsedTitle, date: parsedDate };
    };

    switch (entry.action) {
      case 'calendar_entry_created': {
        return `Dodano wpis dla ${childName}${date ? ` na dzień ${date}` : ''}`;
      }
      case 'calendar_entry_updated': {
        const changeStr = change ? `: ${change}` : '';
        return `Zmieniono wpis dla ${childName}${date ? ` na dzień ${date}` : ''}${changeStr}`;
      }
      case 'calendar_entry_deleted': {
        return `Usunięto wpis dla ${childName}${date ? ` z dnia ${date}` : ''}`;
      }
      // Events / Notes / Expenses (drugi widok)
      case 'event_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Dodano wydarzenie${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'event_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const changeStr = change ? `: ${change}` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Zaktualizowano wydarzenie${title}${child}${d ? ` (${d})` : ''}${changeStr}`;
      }
      case 'event_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Usunięto wydarzenie${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Dodano notatkę${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Zaktualizowano notatkę${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Usunięto notatkę${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'expense_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const amount = entry.metadata?.amount ? ` ${entry.metadata.amount} ${entry.metadata.currency || 'PLN'}` : '';
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Dodano koszt${title}${child}${amount}${d ? ` (${d})` : ''}`;
      }
      case 'expense_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const parts: string[] = [];
        if (entry.metadata?.old_amount !== undefined || entry.metadata?.new_amount !== undefined) {
          parts.push(`kwota: ${valueToLabel(entry.metadata?.old_amount)} → ${valueToLabel(entry.metadata?.new_amount)}`);
        }
        if (entry.metadata?.old_currency || entry.metadata?.new_currency) {
          parts.push(`waluta: ${entry.metadata?.old_currency || '—'} → ${entry.metadata?.new_currency || '—'}`);
        }
        const suffix = parts.length ? ` (${parts.join(', ')})` : '';
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Zaktualizowano koszt${title}${child}${d ? ` (${d})` : ''}${suffix}`;
      }
      case 'expense_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` „${t}”` : '';
        const child = childName ? ` dla ${childName}` : '';
        return `Usunięto koszt${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'member_added':
        return `Dodano użytkownika ${entry.metadata.email || ''} jako ${formatRole(entry.metadata.role)}`;
      case 'member_removed':
        return `Usunięto użytkownika ${entry.metadata.email || ''} (${formatRole(entry.metadata.role)})`;
      case 'member_role_changed':
        return `Zmieniono rolę użytkownika ${entry.metadata.email || ''} z ${formatRole(entry.metadata.old_role)} na ${formatRole(entry.metadata.new_role)}`;
      case 'child_added':
        return `Dodano dziecko: ${entry.metadata.name || ''}`;
      case 'child_updated':
        return `Zaktualizowano dane dziecka: ${entry.metadata.name || ''}`;
      case 'child_removed':
        return `Usunięto dziecko: ${entry.metadata.name || ''}`;
      case 'family_settings_updated': {
        const changes: string[] = [];
        if (entry.metadata.name_changed) changes.push('nazwa rodziny');
        if (entry.metadata.home_a_changed) changes.push('nazwa domu A');
        if (entry.metadata.home_b_changed) changes.push('nazwa domu B');
        return `Zmieniono ustawienia: ${changes.join(', ')}`;
      }
      case 'invitation_sent':
        return `Wysłano zaproszenie do ${entry.metadata.email || ''} (rola: ${formatRole(entry.metadata.role)})`;
      case 'invitation_accepted':
        return `Zaproszenie dla ${entry.metadata.email || ''} zostało przyjęte`;
      case 'invitation_rejected':
        return `Zaproszenie dla ${entry.metadata.email || ''} zostało odrzucone`;
      case 'system_update':
        return `Aktualizacja systemu: ${entry.details}`;
      default:
        return entry.details || '';
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 text-sm">Błąd: {error}</p>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <p className="text-gray-600 text-sm">Brak aktywności do wyświetlenia</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activities.map((activity) => (
        <div 
          key={activity.id} 
          className="p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-start">
            <div className="mr-3 mt-1">
              {getActivityIcon(activity.action)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 mb-0.5">
                {formatActivityAction(activity.action)}
              </p>
              <p className="text-xs text-gray-600">
                {formatActivityDetails(activity)}
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(activity.created_at)}
                  <span className="mx-1">•</span>
                  {formatAbsoluteDate(activity.created_at)}
                </span>
                {(activity.user_email || activity.user_display_email) && (
                  <>
                    <span className="mx-1 text-gray-400">•</span>
                    <span className="text-xs text-gray-500">
                      {activity.user_display_email || activity.user_email}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

function formatTimeAgo(dateString: string): string {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true, locale: pl });
  } catch (e) {
    return dateString;
  }
}

function formatAbsoluteDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL');
  } catch (e) {
    return dateString;
  }
}

function getActivityIcon(action: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    'calendar_entry_created': <span className="text-green-500">📅</span>,
    'calendar_entry_updated': <span className="text-blue-500">📅</span>,
    'calendar_entry_deleted': <span className="text-red-500">📅</span>,
    // Wydarzenia
    'event_created': <span className="text-green-500">📌</span>,
    'event_updated': <span className="text-blue-500">📌</span>,
    'event_deleted': <span className="text-red-500">📌</span>,
    // Notatki – kartka z piórem
    'note_created': <span className="text-green-500">📝</span>,
    'note_updated': <span className="text-blue-500">📝</span>,
    'note_deleted': <span className="text-red-500">📝</span>,
    // Koszty – pieniądze
    'expense_created': <span className="text-green-600">💸</span>,
    'expense_updated': <span className="text-blue-600">💸</span>,
    'expense_deleted': <span className="text-red-600">💸</span>,
    'member_added': <span className="text-green-500">👤</span>,
    'member_removed': <span className="text-red-500">👤</span>,
    'member_role_changed': <span className="text-blue-500">👤</span>,
    'child_added': <span className="text-green-500">👶</span>,
    'child_updated': <span className="text-blue-500">👶</span>,
    'child_removed': <span className="text-red-500">👶</span>,
    'family_settings_updated': <span className="text-blue-500">⚙️</span>,
    'invitation_sent': <span className="text-blue-500">📨</span>,
    'invitation_accepted': <span className="text-green-500">✉️</span>,
    'invitation_rejected': <span className="text-red-500">✉️</span>,
    'system_update': <span className="text-purple-500">🔄</span>,
  };

  return iconMap[action] || <span className="text-gray-500">📝</span>;
}

function formatRole(role?: string): string {
  if (!role) return '';
  const roleMap: Record<string, string> = {
    'parent_a': 'Rodzic (administrator)',
    'parent_b': 'Rodzic',
    'caregiver': 'Opiekun',
    'child_viewer': 'Obserwator'
  };
  return roleMap[role] || role;
}

function valueToLabel(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}